import { EditorContent, useEditor } from "@tiptap/react";
import { Dispatch, SetStateAction, useEffect } from "react";

// --- Tiptap Core Extensions ---
import { StarterKit } from "@tiptap/starter-kit";
import { Image } from "@tiptap/extension-image";
import { TaskItem, TaskList } from "@tiptap/extension-list";
import { TextAlign } from "@tiptap/extension-text-align";
import { Typography } from "@tiptap/extension-typography";
import { Highlight } from "@tiptap/extension-highlight";
import { Subscript } from "@tiptap/extension-subscript";
import { Superscript } from "@tiptap/extension-superscript";
import { Placeholder, Selection } from "@tiptap/extensions";

// --- Tiptap Node ---
import { ImageUploadNode } from "components/tiptap-node/image-upload-node/image-upload-node-extension";
import { HorizontalRule } from "components/tiptap-node/horizontal-rule-node/horizontal-rule-node-extension";
import "components/tiptap-node/blockquote-node/blockquote-node.scss";
import "components/tiptap-node/code-block-node/code-block-node.scss";
import "components/tiptap-node/horizontal-rule-node/horizontal-rule-node.scss";
import "components/tiptap-node/list-node/list-node.scss";
import "components/tiptap-node/image-node/image-node.scss";
import "components/tiptap-node/heading-node/heading-node.scss";
import "components/tiptap-node/paragraph-node/paragraph-node.scss";
import "./EventDescriptionEditor.scss";

//
import { TextStyleExtended } from "utils/TipTapCustomExtensions/TextStyleExtended";
import { Color } from "@tiptap/extension-color";
import { FontFamily } from "@tiptap/extension-text-style";
// import { DragHandle } from "@tiptap/extension-drag-handle";
import { useMemo } from "react";

export default function EventDescriptionEditor({
  description,
  setShowSimpleEditorModal,
  handleChange,
}: // setDescription,
{
  description: string | null;
  // setDescription?: Dispatch<SetStateAction<string>>;
  setShowSimpleEditorModal?: Dispatch<SetStateAction<boolean>>;
  handleChange?: (val: string) => void;
}) {
  const defaultContent = "<p></p>";
  const normalizedDescription = useMemo(
    () => (description && description.trim() ? description : defaultContent),
    [description]
  );
  const editor = useEditor({
    immediatelyRender: false,
    shouldRerenderOnTransaction: false,
    editorProps: {
      attributes: {
        autocomplete: "off",
        autocorrect: "off",
        autocapitalize: "off",
        "aria-label": "Main content area, start typing to enter text.",
        class: "simple-editor event-description-editor",
      },
    },
    extensions: [
      StarterKit.configure({
        horizontalRule: false,
        link: {
          openOnClick: false,
          enableClickSelection: true,
        },
      }),
      TextStyleExtended,
      Color.configure({
        types: ["textStyle", "heading", "paragraph"],
      }),
      FontFamily.configure({
        types: ["textStyle"],
      }),
      HorizontalRule,
      TextAlign.configure({ types: ["heading", "paragraph"] }),
      TaskList,
      TaskItem.configure({ nested: true }),
      Highlight.configure({ multicolor: true }),
      Image,
      Typography,
      Superscript,
      Subscript,
      Selection,
      ImageUploadNode.configure({
        accept: "image/*",
        // maxSize: MAX_FILE_SIZE,
        limit: 3,
        // upload: uploadImageToLibrary,
        onError: (error) => console.error("Upload failed:", error),
      }),
      Placeholder.configure({
        placeholder: ({ node }) => {
          // if (node.type.name === "heading") {
          //   return "Click to add title";
          // }
          if (node.type.name === "paragraph") {
            return "Click to add description";
          }
          return "Click to add description";
        },
        emptyEditorClass: "h-[50px] is-simple-editor-empty",
        showOnlyCurrent: false,
        showOnlyWhenEditable: false,
      }),
      // DragHandle.configure({
      //   render: () => {
      //     const element = document.createElement("div");
      //     element.className = "drag-handle-container";
      //     element.innerHTML = `
      //              <div class="drag-handle-buttons">
      //                  <button class="drag-handle-btn" title="Drag to move">⋮⋮</button>
      //              </div>
      //            `;

      //     return element;
      //   },
      // }),
    ],

    // <button class="drag-handle-btn" title="Drag to move">⋮⋮</button>
    // content,
    content: normalizedDescription,
    // content: `<p>The event will feature keynote addresses from renowned agricultural experts, engaging panel discussions on climate-smart farming, agritech innovations, sustainable practices, and the role of youth in transforming agriculture. Attendees will also experience interactive workshops on modern farming techniques, digital tools for agribusiness, and financial literacy for farmers.</p><img src="https://api.teamdocs.io/Document/stream/6fa79f26-a1ca-469a-bf96-8622cb4ae187" alt="Screenshot (579)" title="Screenshot (579)">`,
    // onUpdate: ({ editor }) => setDescription(editor.getHTML()),
    editable: false,
  });

  //   Updating html content once description changes
  useEffect(() => {
    if (!editor) return;
    const currentContent = editor.getHTML();
    if (currentContent !== normalizedDescription) {
      editor.commands.setContent(normalizedDescription, {
        emitUpdate: false,
      });
    }
    if (handleChange && normalizedDescription) {
      handleChange(normalizedDescription);
    }
  }, [editor, handleChange, normalizedDescription]);

  return (
    <section
      className="event-description-editor-wrapper"
      onClick={() => {
        setShowSimpleEditorModal && setShowSimpleEditorModal(true);
      }}
    >
      <EditorContent className="event-description-editor-content" editor={editor} />
    </section>
  );
}
